/**
 * Reader for the structured result payloads the ledger stores in
 * `translated_text`.
 *
 * An image or PDF translation does not store a paragraph of prose. The phone
 * writes a JSON document — a summary card, a parking verdict, a word pair
 * list, or a paged PDF layout — into the SAME column a text translation uses
 * for its plain output. Every surface that had only ever seen prose therefore
 * rendered `{"kind":"summary","title":...}` at the reader, in the result
 * dialog, in the history preview line, and on the clipboard.
 *
 * This mirrors baba-v2 `mobile/components/image/redesign/ImageResultRouter.tsx`
 * (`coerceImageResult`), `mobile/types/pdfResult.ts` (`coercePdfLayout`) and
 * `mobile/utils/historyPreview.ts` (the preview and copy text builders), so
 * the two clients agree on what a row says.
 *
 * Every shape is optional and every field is checked: a row that is prose, is
 * malformed JSON, or is JSON of a kind this build does not know falls back to
 * `{ kind: 'plain' }` carrying the original string. Nothing here throws.
 *
 * Pure and dependency-free (no react, no fetch, no storage) so it is
 * unit-testable the same way as ./gatewayLibraryMapping.ts.
 */

export type SummaryIcon = 'time' | 'place' | 'action' | 'info' | 'warning'

const SUMMARY_ICONS: readonly SummaryIcon[] = ['time', 'place', 'action', 'info', 'warning']

export interface SummaryKeyPoint {
  icon: SummaryIcon
  title: string
  sub?: string
}

/** One bubble of a translated chat screenshot. `me` is the phone's owner. */
export interface ChatMessage {
  side: 'me' | 'them'
  /** Sender name as shown in the bubble (group chats); absent otherwise. */
  sender?: string
  /** Timestamp exactly as the bubble showed it; absent otherwise. */
  time?: string
  text: string
}

/** The image "Standard" result — the shape behind the raw JSON Isaac saw. */
export interface MediaSummary {
  kind: 'summary'
  title: string
  sourceLabel?: string
  tldr: string
  keyPoints: SummaryKeyPoint[]
  /** Often empty — the model returns '' when the image carried no text. */
  fullTranslation: string
  wordCount?: number
  /**
   * Present when the gateway read the image as a conversation (WhatsApp,
   * Telegram, iMessage…): one translated entry per bubble, in order, so the
   * result can be drawn the way the screenshot looked instead of as a block
   * of prose. `baba-v2 server/_shared/chatTranscript.ts` is where the sides
   * come from. Empty for every other kind of image.
   */
  messages: ChatMessage[]
}

export type ParkingVerdict = 'ok' | 'no' | 'caution'

export interface ParkingScheduleRow {
  label: string
  value: string
  muted?: boolean
}

export interface MediaParking {
  kind: 'parking'
  verdict: ParkingVerdict
  headline: string
  detail: string
  validUntil?: string
  timeRemaining?: string
  signLabel?: string
  schedule: ParkingScheduleRow[]
}

export interface WordPair {
  he: string
  en: string
}

export interface MediaWordForWord {
  kind: 'word-for-word'
  pairs: WordPair[]
}

export interface PdfPage {
  index: number
  heading?: string
  source: string[]
  target: string[]
}

/** The PDF layout carries no `kind` discriminator — it is recognised by its
 * `pages` array, same as mobile's `coercePdfLayout`. */
export interface MediaPdf {
  kind: 'pdf'
  pages: PdfPage[]
  generatedName?: string
}

/** Prose, or anything this build could not read. `text` is the original. */
export interface MediaPlain {
  kind: 'plain'
  text: string
}

export type MediaResult = MediaSummary | MediaParking | MediaWordForWord | MediaPdf | MediaPlain

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function optionalStr(v: unknown): string | undefined {
  const s = str(v).trim()
  return s ? s : undefined
}

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.map(str).filter((s) => s.trim() !== '') : []
}

function keyPoints(v: unknown): SummaryKeyPoint[] {
  if (!Array.isArray(v)) return []
  const points: SummaryKeyPoint[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const title = str(o.title).trim()
    if (!title) continue
    const icon = str(o.icon) as SummaryIcon
    points.push({
      icon: SUMMARY_ICONS.includes(icon) ? icon : 'info',
      title,
      sub: optionalStr(o.sub),
    })
  }
  return points
}

/**
 * The gateway numbers bubbles `i = 1..n` in order (one per line of the
 * reader's transcript). A list with a gap, a repeat, a blank bubble or a
 * malformed entry is a conversation with a hole in it, and a bubble view
 * would hide the hole; such a list is discarded whole so the card falls back
 * to `fullTranslation`, which the same response always carries.
 */
function chatMessages(v: unknown): ChatMessage[] {
  if (!Array.isArray(v) || v.length === 0) return []
  const messages: ChatMessage[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') return []
    const o = raw as Record<string, unknown>
    const text = str(o.text).trim()
    if (!text || o.i !== messages.length + 1) return []
    messages.push({
      side: o.side === 'me' ? 'me' : 'them',
      sender: optionalStr(o.sender),
      time: optionalStr(o.time),
      text,
    })
  }
  return messages
}

function schedule(v: unknown): ParkingScheduleRow[] {
  if (!Array.isArray(v)) return []
  const rows: ParkingScheduleRow[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const label = str(o.label).trim()
    const value = str(o.value).trim()
    if (!label && !value) continue
    rows.push({ label, value, muted: o.muted === true })
  }
  return rows
}

function pdfPages(v: unknown): PdfPage[] {
  if (!Array.isArray(v)) return []
  const pages: PdfPage[] = []
  for (const [i, raw] of v.entries()) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const source = stringList(o.source)
    const target = stringList(o.target)
    if (source.length === 0 && target.length === 0) continue
    pages.push({
      index: typeof o.index === 'number' ? o.index : i,
      heading: optionalStr(o.heading),
      source,
      target,
    })
  }
  return pages
}

/** JSON only ever starts with a brace or a bracket. Anything else is prose,
 * and prose must not pay for a try/catch on every render. */
function looksLikeJson(raw: string): boolean {
  const s = raw.trim()
  return s.startsWith('{') || s.startsWith('[')
}

function tryParseObject(raw: string): Record<string, unknown> | null {
  const s = raw.trim()
  if (!looksLikeJson(s)) return null
  try {
    const parsed: unknown = JSON.parse(s)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/**
 * Reads one `translated_text` value. Never throws: an unreadable value comes
 * back as `{ kind: 'plain' }` holding the original string, so a caller can
 * always render something truthful.
 */
export function parseMediaResult(translatedText: string | null | undefined): MediaResult {
  const text = str(translatedText)
  const o = tryParseObject(text)
  if (!o) return { kind: 'plain', text }

  if (o.kind === 'summary' && typeof o.tldr === 'string') {
    return {
      kind: 'summary',
      title: str(o.title).trim(),
      sourceLabel: optionalStr(o.sourceLabel),
      tldr: o.tldr,
      keyPoints: keyPoints(o.keyPoints),
      fullTranslation: str(o.fullTranslation),
      wordCount: typeof o.wordCount === 'number' && Number.isFinite(o.wordCount) ? o.wordCount : undefined,
      messages: chatMessages(o.messages),
    }
  }

  if (o.kind === 'parking' && typeof o.headline === 'string') {
    const verdict = str(o.verdict)
    return {
      kind: 'parking',
      verdict: verdict === 'ok' || verdict === 'no' ? verdict : 'caution',
      headline: o.headline,
      detail: str(o.detail),
      validUntil: optionalStr(o.validUntil),
      timeRemaining: optionalStr(o.timeRemaining),
      signLabel: optionalStr(o.signLabel),
      schedule: schedule(o.schedule),
    }
  }

  if (o.kind === 'word-for-word' && Array.isArray(o.pairs)) {
    const pairs: WordPair[] = []
    for (const raw of o.pairs) {
      if (!raw || typeof raw !== 'object') continue
      const p = raw as Record<string, unknown>
      const he = str(p.he)
      const en = str(p.en)
      if (!he && !en) continue
      pairs.push({ he, en })
    }
    return { kind: 'word-for-word', pairs }
  }

  // The PDF layout has no `kind`; `pages` is what identifies it.
  const pages = pdfPages(o.pages)
  if (pages.length > 0) {
    return { kind: 'pdf', pages, generatedName: optionalStr(o.generatedName) }
  }

  // Readable JSON of an unknown shape. Showing the raw braces is the bug this
  // module exists to fix, so it degrades to the original string and lets the
  // caller decide (an empty preview beats a wall of punctuation).
  return { kind: 'plain', text }
}

/**
 * Chat bubbles from a card that is still being written. Unlike
 * `chatMessages`, a list that stops early is expected here, so it keeps the
 * run of well-formed bubbles up to the first one that is not complete yet
 * (no text yet, or numbered out of order) instead of discarding the lot.
 */
function partialChatMessages(v: unknown): ChatMessage[] {
  if (!Array.isArray(v)) return []
  const messages: ChatMessage[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') break
    const o = raw as Record<string, unknown>
    const text = str(o.text).trim()
    if (!text || (o.i !== undefined && o.i !== messages.length + 1)) break
    messages.push({
      side: o.side === 'me' ? 'me' : 'them',
      sender: optionalStr(o.sender),
      time: optionalStr(o.time),
      text,
    })
  }
  return messages
}

/**
 * Reads one `partialStructured` frame from the image gateway: the card the
 * model is still writing, re-sent every ~200ms. Same shape as the final
 * document, but any field may be missing, a string may stop mid-word and an
 * array may lack its trailing items. `kind` normally arrives first; when it
 * has not, the fields present decide.
 *
 * Returns null until the partial holds something worth drawing (a title, a
 * tldr, a pair, a bubble, a headline…) or when the value is not an object at
 * all. Never throws.
 */
export function parsePartialMediaResult(value: unknown): MediaResult | null {
  try {
    const o =
      typeof value === 'string'
        ? tryParseObject(value)
        : value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : null
    if (!o) return null
    const kind = typeof o.kind === 'string' ? o.kind : null

    if (kind === 'word-for-word' || (kind === null && Array.isArray(o.pairs))) {
      const pairs: WordPair[] = []
      if (Array.isArray(o.pairs)) {
        for (const raw of o.pairs) {
          if (!raw || typeof raw !== 'object') continue
          const p = raw as Record<string, unknown>
          const he = str(p.he)
          const en = str(p.en)
          if (!he.trim() && !en.trim()) continue
          pairs.push({ he, en })
        }
      }
      return pairs.length > 0 ? { kind: 'word-for-word', pairs } : null
    }

    if (kind === 'parking' || (kind === null && typeof o.headline === 'string')) {
      const headline = str(o.headline)
      const detail = str(o.detail)
      const rows = schedule(o.schedule)
      if (!headline.trim() && !detail.trim() && rows.length === 0) return null
      const verdict = str(o.verdict)
      return {
        kind: 'parking',
        verdict: verdict === 'ok' || verdict === 'no' ? verdict : 'caution',
        headline,
        detail,
        validUntil: optionalStr(o.validUntil),
        timeRemaining: optionalStr(o.timeRemaining),
        signLabel: optionalStr(o.signLabel),
        schedule: rows,
      }
    }

    if (kind === 'summary' || kind === null) {
      const summary: MediaSummary = {
        kind: 'summary',
        title: str(o.title).trim(),
        sourceLabel: optionalStr(o.sourceLabel),
        tldr: str(o.tldr),
        keyPoints: keyPoints(o.keyPoints),
        fullTranslation: str(o.fullTranslation),
        wordCount: typeof o.wordCount === 'number' && Number.isFinite(o.wordCount) ? o.wordCount : undefined,
        messages: partialChatMessages(o.messages),
      }
      const meaningful =
        !!summary.title ||
        !!summary.tldr.trim() ||
        summary.keyPoints.length > 0 ||
        !!summary.fullTranslation.trim() ||
        summary.messages.length > 0
      return meaningful ? summary : null
    }

    return null
  } catch {
    return null
  }
}

/**
 * Reads the `pages` array the gateway's PDF handler hands back on its
 * `complete` SSE frame, for a translation that has JUST finished rather than
 * one being re-read from the ledger.
 *
 * Both inputs describe the same document, but they arrive differently: the
 * ledger stores the model's raw JSON string (which `parseMediaResult` reads),
 * while the live stream also carries the array the SERVER already parsed with
 * its own more forgiving reader (it strips a ```json fence, which
 * `looksLikeJson` above deliberately refuses). Preferring the server's array
 * means a fenced response still renders as pages instead of degrading to a
 * wall of raw JSON.
 *
 * Accepts the array itself or a wrapper object holding it, matching mobile's
 * `coercePdfLayout(obj.pages ?? obj, ...)` in `mobile/types/pdfResult.ts`.
 * Returns null when nothing usable is in there, so the caller can fall back.
 */
export function coercePdfLayout(
  input: unknown,
  generatedName?: string | null,
): MediaPdf | null {
  const raw =
    input && typeof input === 'object' && !Array.isArray(input)
      ? (input as Record<string, unknown>).pages
      : input
  const pages = pdfPages(raw)
  if (pages.length === 0) return null
  return { kind: 'pdf', pages, generatedName: optionalStr(generatedName) }
}

/**
 * The one-line preview a history row shows under its title. Mirrors mobile's
 * `getHistoryPreview`: the human sentence, never the markup around it.
 */
export function mediaResultPreview(result: MediaResult): string {
  switch (result.kind) {
    case 'summary':
      return result.tldr.trim() || result.title
    case 'parking':
      return [result.headline, result.detail].filter(Boolean).join(' — ')
    case 'word-for-word':
      return result.pairs.map((p) => p.en).filter(Boolean).join(' · ')
    case 'pdf':
      return result.pages.flatMap((p) => p.target).join(' ').trim() || result.generatedName || ''
    case 'plain':
      // Truncated or unreadable JSON reaches here holding its own braces.
      // A preview line is for reading, and `{"kind":"summary","title":"N…` is
      // the exact thing this module exists to keep out of the sidebar, so the
      // row falls back to its type label alone. `mediaResultCopyText` still
      // hands back the original, so nothing is destroyed.
      return looksLikeJson(result.text) ? '' : result.text
  }
}

/**
 * What Copy puts on the clipboard and what Listen reads aloud. Mirrors
 * mobile's `getHistoryCopyText`: prose only, so neither action ever hands a
 * person (or a speech synthesiser) a JSON document.
 *
 * Key points, source label and word count are deliberately absent — they are
 * navigation for the eye, and mobile leaves them out of copied text too.
 */
export function mediaResultCopyText(result: MediaResult): string {
  switch (result.kind) {
    case 'summary':
      // A conversation copies as a transcript, one `Sender: message` line per
      // bubble, which is how a person would paste a chat into an email.
      if (result.messages.length > 0) {
        const transcript = result.messages
          .map((m) => `${m.sender ?? (m.side === 'me' ? 'Me' : 'Them')}: ${m.text}`)
          .join('\n')
        return [result.tldr.trim(), transcript].filter(Boolean).join('\n\n')
      }
      return [result.tldr, result.fullTranslation].map((s) => s.trim()).filter(Boolean).join('\n\n')
    case 'parking':
      return [result.headline, result.detail].filter(Boolean).join('\n')
    case 'word-for-word':
      return result.pairs.map((p) => p.en).filter(Boolean).join('\n')
    case 'pdf':
      return result.pages.flatMap((p) => p.target).join('\n').trim()
    case 'plain':
      return result.text
  }
}

/** Convenience for a caller holding a raw column value and wanting one line. */
export function previewFromTranslatedText(translatedText: string | null | undefined): string {
  return mediaResultPreview(parseMediaResult(translatedText))
}

/** Convenience for a caller holding a raw column value and wanting prose. */
export function copyTextFromTranslatedText(translatedText: string | null | undefined): string {
  return mediaResultCopyText(parseMediaResult(translatedText))
}
